import { useEffect, useState, Fragment } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Loader2, ScrollText, ShieldCheck, ShieldAlert, Search, ChevronDown, ChevronRight } from "lucide-react";

const CAT_LABELS = {
  brand: "Brand Profile", product: "Product", image: "Image", price: "Pricing",
  showcase: "Showcase", approval: "Approval", user: "User", permission: "Permission",
  brand_suspension: "Brand Suspension", product_archival: "Product Archival",
};
const CAT_COLORS = {
  brand: "bg-sage/30 text-forest", product: "bg-olive/15 text-olive", image: "bg-mustard/20 text-earth",
  price: "bg-terracotta/15 text-terracotta", showcase: "bg-terracotta/25 text-terracotta",
  approval: "bg-emerald-100 text-emerald-700", user: "bg-forest/10 text-forest",
  permission: "bg-forest/15 text-forest", brand_suspension: "bg-red-100 text-red-700",
  product_archival: "bg-brown/15 text-brown/60",
};

function fmt(v) {
  if (v === null || v === undefined || v === "") return "—";
  if (Array.isArray(v)) return v.length ? v.join(", ") : "—";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

export default function AdminAudit() {
  const [data, setData] = useState(null);
  const [denied, setDenied] = useState(false);
  const [cat, setCat] = useState("all");
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState({});
  const [integrity, setIntegrity] = useState(null);

  const load = () => {
    const params = new URLSearchParams();
    if (cat !== "all") params.set("category", cat);
    if (search) params.set("search", search);
    params.set("limit", "300");
    api.get(`/admin/audit?${params.toString()}`).then((r) => setData(r.data)).catch((e) => {
      if (e.response?.status === 403) setDenied(true);
      else setData({ items: [], counts: {}, total: 0 });
    });
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [cat]);

  const verify = () => api.get("/admin/audit/verify").then((r) => {
    setIntegrity(r.data);
    r.data.ok ? toast.success(`Audit chain verified — ${r.data.total} entries intact`) : toast.error(`Tampering detected at entry #${r.data.broken_at_seq}`);
  }).catch(() => toast.error("Could not verify"));

  if (denied) return (
    <div className="max-w-lg mx-auto text-center py-24" data-testid="audit-access-denied">
      <ShieldAlert className="w-12 h-12 text-terracotta mx-auto mb-4" />
      <h1 className="font-heading text-2xl text-forest mb-2">Access restricted</h1>
      <p className="text-sm text-brown/60">The audit trail can only be viewed by the Super Admin.</p>
    </div>
  );
  if (!data) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-forest" /></div>;

  const cats = ["all", ...Object.keys(CAT_LABELS)];

  return (
    <div className="max-w-6xl" data-testid="admin-audit-page">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-1">
        <h1 className="font-heading text-3xl lg:text-4xl text-forest tracking-tight flex items-center gap-2"><ScrollText className="w-7 h-7 text-olive" /> Audit Trail</h1>
        <button onClick={verify} data-testid="verify-audit-btn" className={`inline-flex items-center gap-1.5 text-sm rounded-full px-4 py-2 border ${integrity && !integrity.ok ? "border-red-300 text-red-600 bg-red-50" : "border-sage text-forest hover:bg-sage/10"}`}>
          {integrity && !integrity.ok ? <ShieldAlert className="w-4 h-4" /> : <ShieldCheck className="w-4 h-4" />} Verify integrity
        </button>
      </div>
      <p className="text-sm text-stone-500 mb-5">Immutable, tamper-evident record of every sensitive change. {data.total} total entries.</p>

      <div className="flex flex-wrap gap-2 mb-3">
        {cats.map((c) => (
          <button key={c} onClick={() => setCat(c)} data-testid={`audit-cat-${c}`}
            className={`px-3 py-1.5 rounded-full text-xs font-500 border transition-colors ${cat === c ? "bg-forest text-cream border-forest" : "bg-white text-brown/70 border-stone-200 hover:border-sage"}`}>
            {c === "all" ? "All" : CAT_LABELS[c]}{c !== "all" && data.counts[c] ? ` (${data.counts[c]})` : ""}
          </button>
        ))}
      </div>

      <div className="relative mb-4 max-w-sm">
        <Search className="w-4 h-4 text-brown/40 absolute left-3 top-1/2 -translate-y-1/2" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Search action, item or user…" className="rounded-xl bg-white pl-9" data-testid="audit-search" />
      </div>

      <div className="bg-white rounded-2xl soft-shadow overflow-hidden">
        {data.items.length === 0 ? <p className="text-sm text-brown/60 py-12 text-center">No audit entries yet.</p> : (
          <table className="w-full text-sm" data-testid="audit-table">
            <thead className="bg-sand text-brown/70 text-xs uppercase tracking-wide">
              <tr><th className="px-3 py-2.5 w-8"></th><th className="text-left px-3 py-2.5">When</th><th className="text-left px-3 py-2.5">Category</th><th className="text-left px-3 py-2.5">Action</th><th className="text-left px-3 py-2.5">Item</th><th className="text-left px-3 py-2.5">By</th></tr>
            </thead>
            <tbody>
              {data.items.map((it) => {
                const open = expanded[it.id];
                return (
                  <Fragment key={it.id}>
                    <tr className="border-t border-sage/15 cursor-pointer hover:bg-sand/40" onClick={() => setExpanded((e) => ({ ...e, [it.id]: !e[it.id] }))} data-testid={`audit-row-${it.seq}`}>
                      <td className="px-3 py-2.5 text-brown/40">{it.changes?.length ? (open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />) : null}</td>
                      <td className="px-3 py-2.5 text-brown/70 whitespace-nowrap">{new Date(it.at).toLocaleString()}</td>
                      <td className="px-3 py-2.5"><Badge className={`${CAT_COLORS[it.category] || "bg-sand text-brown"} hover:opacity-100`}>{CAT_LABELS[it.category] || it.category}</Badge></td>
                      <td className="px-3 py-2.5 text-forest font-500">{it.action}{it.reason ? <span className="block text-xs text-terracotta font-400">Reason: {it.reason}</span> : null}</td>
                      <td className="px-3 py-2.5 text-brown/80">{it.entity_name || "—"}</td>
                      <td className="px-3 py-2.5 text-brown/80">{it.user_name}<span className="block text-[10px] text-brown/50">{it.user_role}</span></td>
                    </tr>
                    {open && it.changes?.length > 0 && (
                      <tr className="bg-sand/30 border-t border-sage/10" data-testid={`audit-changes-${it.seq}`}>
                        <td></td>
                        <td colSpan={5} className="px-3 py-3">
                          <div className="space-y-1.5">
                            {it.changes.map((ch, i) => (
                              <div key={i} className="grid grid-cols-[140px,1fr,1fr] gap-2 text-xs items-start">
                                <span className="font-600 text-brown/70">{ch.field}</span>
                                <span className="text-red-600 line-through break-words">{fmt(ch.old)}</span>
                                <span className="text-emerald-700 break-words">→ {fmt(ch.new)}</span>
                              </div>
                            ))}
                          </div>
                          <p className="text-[10px] text-brown/40 mt-2 font-mono">entry #{it.seq} · hash {String(it.hash).slice(0, 16)}…</p>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
